/**
 * auditTokenFetchShim.js — CHG-113, hardened per issue #31.
 *
 * Demo-critical shim: forwards the audit-link `?token=...` from the
 * current browser URL to every `/api/analyses/{id}/...` fetch so
 * anonymous share-link recipients can load every tab endpoint without
 * cookies.
 *
 * Backend `_assert_can_view_analysis(db, id, user, token=token)` already
 * verifies the signed JWT-style token via `audit_link.verify(token, id)`
 * and returns the doc without needing session auth. All 22
 * `?token=`-accepting endpoints were verified via `curl -X POST` /
 * `curl` against the preview environment — 200 OK on every panel.
 *
 * The bug this closes: dashboard tab components each call
 * `fetch(${API}/api/analyses/${id}/${panel})` directly. None of them
 * inspect the current URL for the token — the token only made it as
 * far as the initial `getAnalysis(id, {token})` call inside
 * `AnalysisDetail.jsx`. Everything else (Brief, Cascade, Historical
 * Analogues, Strategic Lens, Risk Profile, Consequence Map, Next Steps,
 * Audit Chain, Full Report / Download) fetched without `?token=` and
 * hit 401 / 404 for the anonymous recipient.
 *
 * Rather than touch 15+ files individually for the 10-hour demo
 * window, we monkey-patch `window.fetch` once at app boot. The patch:
 *   • extracts the token from `window.location.search`
 *   • inspects the outgoing URL — passes anything that isn't an
 *     `/api/analyses/...` call through unchanged
 *   • otherwise appends `?token=…` if not already present
 *   • is a strict superset of the pre-shim behaviour — the token can
 *     ONLY expand permissions; owner-with-cookie calls still succeed
 *     with or without it.
 *
 * Issue #31 hardening (interim fix — see the issue for the full
 * ~65-file migration this defers): the original match test was a bare
 * substring regex against the raw URL string, with no origin check and
 * no method restriction. An absolute cross-origin URL that merely
 * *contained* `/api/analyses/` as a substring would have the token
 * attached and sent off-origin, and the token was attached to
 * mutating (`POST`/`PATCH`/`DELETE`) requests too — broader than the
 * read-only grant the share modal describes. Not exploitable today
 * (no attacker-controlled fetch target exists in the tree), but this
 * is a global override of a browser primitive, so it's hardened now
 * rather than left to inherit risk from a future code path. Three
 * changes:
 *   1. Origin allowlist — the resolved request origin must match the
 *      app's own backend origin (`REACT_APP_BACKEND_URL`, or the page's
 *      own origin when that's unset/relative, matching `lib/api.js`'s
 *      own resolution rule) before the token is ever attached.
 *   2. Path anchoring — match on `new URL(...).pathname` starting with
 *      `/api/analyses/`, not a substring test against the raw string.
 *   3. Method restriction — only GET/HEAD requests get the token;
 *      mutating methods are passed through unchanged.
 */

// Match ANALYSES-scoped API paths, anchored at the start of the path
// (not a substring test against the raw URL string).
const RX_ANALYSES_PATH = /^\/api\/analyses\//;

/**
 * The one origin fetches are allowed to reach with the token attached.
 * Mirrors `lib/api.js`'s own BASE resolution: an unset/blank
 * REACT_APP_BACKEND_URL is the default correct configuration, meaning
 * relative `/api/...` calls resolve against the page's own origin.
 */
export function resolveAllowedOrigin() {
    const base = process.env.REACT_APP_BACKEND_URL;
    try {
        return base
            ? new URL(base, window.location.origin).origin
            : window.location.origin;
    } catch {
        return window.location.origin;
    }
}

export function getUrlTokenFromLocation() {
    try {
        const sp = new URLSearchParams(window.location.search || "");
        const t = sp.get("token");
        if (!t || typeof t !== "string") return null;
        // Sanity guard — audit tokens are opaque base64url; reject
        // anything with a query-string delimiter to avoid injection
        // from a crafted URL.
        if (t.includes("&") || t.includes("=")) return null;
        return t;
    } catch { return null; }
}

/** Only GET/HEAD requests are eligible — mutating methods never get the token. */
export function isSafeMethod(init) {
    const method = String((init && init.method) || "GET").toUpperCase();
    return method === "GET" || method === "HEAD";
}

function resolveUrl(inputUrl) {
    try { return new URL(inputUrl, window.location.origin); } catch { return null; }
}

/**
 * True only when `inputUrl` resolves to the allowed origin AND its path
 * starts with `/api/analyses/`. Both checks are done against a parsed
 * `URL`, never a substring test on the raw string.
 */
export function isEligibleAnalysesRequest(inputUrl, allowedOrigin) {
    const u = resolveUrl(inputUrl);
    if (!u) return false;
    if (u.origin !== allowedOrigin) return false;
    return RX_ANALYSES_PATH.test(u.pathname);
}

export function attachToken(inputUrl, token) {
    try {
        const u = new URL(inputUrl, window.location.origin);
        // Don't clobber an existing token param — some callers pass
        // it explicitly (e.g. the getAnalysis helper). First-write
        // wins.
        if (u.searchParams.has("token")) return inputUrl;
        u.searchParams.set("token", token);
        // Return absolute URL when the input was absolute; return
        // pathname+search when the input was relative. Preserves
        // any Request-object semantics the caller relied on.
        return /^https?:/i.test(inputUrl)
            ? u.toString()
            : `${u.pathname}${u.search}${u.hash}`;
    } catch {
        // Malformed URL — pass through untouched.
        return inputUrl;
    }
}

export function installAuditTokenFetchShim() {
    if (typeof window === "undefined" || !window.fetch) return;
    if (window.__audit_token_fetch_shim_installed__) return;

    const origFetch = window.fetch.bind(window);
    const allowedOrigin = resolveAllowedOrigin();

    window.fetch = function patchedFetch(input, init) {
        const token = getUrlTokenFromLocation();
        if (!token) return origFetch(input, init);
        if (!isSafeMethod(init)) return origFetch(input, init);

        // Two input shapes: string / URL / Request. We only rewrite
        // string / URL — Request objects have a locked URL and are
        // rarely used in this codebase.
        if (typeof input === "string" && isEligibleAnalysesRequest(input, allowedOrigin)) {
            return origFetch(attachToken(input, token), init);
        }
        if (input instanceof URL && isEligibleAnalysesRequest(input.toString(), allowedOrigin)) {
            return origFetch(attachToken(input.toString(), token), init);
        }
        return origFetch(input, init);
    };

    window.__audit_token_fetch_shim_installed__ = true;
}

installAuditTokenFetchShim();

export {};
